import { Box, HStack, VStack, useTheme, Image, IconButton, ScrollView, FlatList } from 'native-base';
import { isEmpty, map, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { ArrowSquareUpRightIcon, Button, CloseIcon, Text } from '@pimm/base';
import { useGetDashboardInfo } from '../hooks/get-delivery-monitor.hook';
import { ResourceLoader } from '@app/components/shared';
import { NetworkBadge } from './network-badge';
import { useSiteConfig } from '@app/features/store-core';

type DeliveryMonitorProps = {
  _container?: React.ComponentProps<typeof VStack>;
  onClose: () => void;
};

export const DeliveryMonitor = ({ _container, onClose }: DeliveryMonitorProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { siteConfig } = useSiteConfig();
  const { data, isLoading, isRefetching } = useGetDashboardInfo(siteConfig?.stopId);

  const calculateDuration = (lastModifiedOnUtc?: string) => {
    if (!lastModifiedOnUtc) return '0d 0h 0m';
    const now = moment.utc().utcOffset(siteConfig?.utcOffsetInMinutes ?? 0);
    const lastModified = moment.utc(lastModifiedOnUtc).utcOffset(siteConfig?.utcOffsetInMinutes ?? 0);
    const diff = moment.duration(now.diff(lastModified));
    return `${diff.days()}d ${diff.hours()}h ${diff.minutes()}m`;
  };

  const items = orderBy(data?.subVendorStatuses, [item => item.status !== 'offline'], ['asc']);

  const handlePressOpenPortal = (url?: string) => {
    if (url) {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  return (
    <VStack space={3} py={1} {..._container} minH={350} maxH={600}>
      <HStack justifyContent="space-between" alignItems="center" py={2}>
        <Image
          source={{ uri: data?.providerLogoUrl }}
          style={{ width: 120, height: 45 }}
          resizeMode="contain"
          resizeMethod="auto"
          alt={data?.providerName ?? ''}
        />

        <HStack space={3}>
          <Button
            size="md"
            endIcon={<ArrowSquareUpRightIcon size="16px" color="black" />}
            onPress={() => handlePressOpenPortal(data?.portalUrl)}
            disabled={isEmpty(data?.portalUrl)}
          >
            {t('delivery_monitor:vendor_portal')}
          </Button>
          <IconButton rounded="sm" p={1} onPress={onClose} icon={<CloseIcon size={4} color={colors.gray[700]} />} />
        </HStack>
      </HStack>
      <ResourceLoader
        flex={1}
        isLoading={isLoading}
        isEmpty={false}
        emptyMessage={isLoading ? t('app:please_wait') : t('delivery_monitor:no_data_found')}
      >
        <Box flex={1}>
          <FlatList
            flex={1}
            w="full"
            data={items}
            keyExtractor={(item, index) => `${item.providerName}.${index}`}
            // TODO: Need to revisit
            // refreshControl={<RefreshControl refreshing={isLoading} onRefresh={handleRefresh} />}
            ListEmptyComponent={
              <Box alignItems="center" justifyContent="center" h={300} py={8}>
                <Text size="md" color="gray.500" fontWeight={500}>
                  {isRefetching ? t('app:please_wait') : t('delivery_monitor:no_data_found')}
                </Text>
              </Box>
            }
            _contentContainerStyle={{ paddingBottom: 8 }}
            renderItem={({ item, index }) => {
              const isOffline = item.status === 'offline';
              return isOffline ? (
                <VStack
                  w="full"
                  bg="white"
                  shadow="1"
                  rounded="lg"
                  p={0}
                  borderWidth={1}
                  borderColor="gray.200"
                  mb={3}
                >
                  <HStack roundedTop="lg" bgColor="error.500" h="50px" alignItems="center" px={3} justifyContent="space-between">
                    <HStack space={3} alignItems="center">
                      <Image
                        source={{ uri: item.providerLogoUrl }}
                        style={{ width: 40, height: 40 }}
                        resizeMode="contain"
                        resizeMethod="auto"
                        alt={item?.providerName ?? ''}
                      />
                      <Text size="lg" fontWeight={700} color="white">
                        {item.providerName}
                      </Text>
                      <NetworkBadge status={item.status as 'offline' | 'online'} />
                    </HStack>

                    <IconButton
                      rounded="md"
                      size={8}
                      bgColor="white"
                      p={1}
                      icon={<ArrowSquareUpRightIcon size={4} color={isOffline ? colors.error[600] : colors.gray[600]} />}
                      onPress={() => handlePressOpenPortal(item.portalUrl)}
                    />
                  </HStack>
                  <HStack w="full" bgColor="gray.100" px={3} py={1}>
                    <Box w="20%">
                      <Text size="sm" fontWeight={500} color="gray.600">
                        {t('delivery_monitor:service_offline_start')}
                      </Text>
                    </Box>
                    <Box w="15%">
                      <Text size="sm" fontWeight={500} color="gray.600">
                        {t('delivery_monitor:duration')}
                      </Text>
                    </Box>
                    <Box w="35%">
                      <Text size="sm" fontWeight={500} color="gray.600">
                        {t('delivery_monitor:reason')}
                      </Text>
                    </Box>
                    <Box w="30%">
                      <Text size="sm" fontWeight={500} color="gray.600">
                        {t('delivery_monitor:action_required')}
                      </Text>
                    </Box>
                  </HStack>
                  <HStack w="full" px={3} py={2}>
                    <Box w="20%">
                      <Text size="md" fontWeight={500} color="gray.700">
                        {item.lastModifiedOnUtc
                          ? moment
                            .utc(item.lastModifiedOnUtc)
                            .utcOffset(siteConfig?.address?.utcOffsetInMinutes ?? 0)
                            .format('M/D/YY h:mmA')
                          : ''}
                      </Text>
                    </Box>
                    <Box w="15%">
                      <Text size="md" fontWeight={500} color="gray.700">
                        {calculateDuration(item.lastModifiedOnUtc)}
                      </Text>
                    </Box>
                    <Box w="35%">
                      <Text size="md" fontWeight={500} color="gray.700">
                        {item.message}
                      </Text>
                    </Box>
                    <Box w="30%">
                      <Text size="md" fontWeight={500} color="gray.700">
                        {item.action}
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              ) : (
                <VStack w="full" bg="white" shadow="1" rounded="lg" p={0} borderWidth={1} borderColor="gray.200" mb={3}>
                  <HStack roundedTop="lg" h="50px" alignItems="center" px={3} justifyContent="space-between">
                    <HStack space={3} alignItems="center">
                      <Image
                        source={{ uri: item.providerLogoUrl }}
                        style={{ width: 40, height: 40 }}
                        resizeMode="contain"
                        resizeMethod="auto"
                        alt={item?.providerName ?? ''}
                      />
                      <Text size="lg" fontWeight={700} color={isOffline ? 'white' : 'black'}>
                        {item.providerName}
                      </Text>
                      <NetworkBadge status={item.status as 'Offline' | 'Online'} />
                    </HStack>
                    <IconButton
                      rounded="md"
                      size={8}
                      borderColor="gray.100"
                      shadow={1}
                      borderWidth={1}
                      bgColor="white"
                      p={1}
                      onPress={() => handlePressOpenPortal(item.portalUrl)}
                      icon={<ArrowSquareUpRightIcon size={4} color={isOffline ? colors.error[600] : colors.gray[600]} />}
                    />
                  </HStack>
                </VStack>
              );
            }}
          />
        </Box>
      </ResourceLoader>
    </VStack>
  );
};
